import { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography,
} from '@mui/material';
import { CheckCircle } from 'lucide-react';
import { changeEntraPasswordViaGraph } from '../auth/runtimeAuth';
import { passwordRuleError, graphErrorMessage, clientErrorMessage } from '../auth/entraPassword';
import { primaryButtonStyle, secondaryButtonStyle, dialogActionsStyle } from '../utils/sharedStyles';
import PasswordInput from './PasswordInput';
import passwordIcon from '../assets/users/password.svg';

const EMPTY_FORM = { current: '', next: '', confirm: '' };

const EntraChangePasswordDialog = ({ open, onClose }) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [error, setError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isDone, setIsDone] = useState(false);

  const updateField = (field, value) => {
    setForm(previous => ({ ...previous, [field]: value }));
    setError('');
  };

  const close = () => {
    if (isSaving) return;
    setForm(EMPTY_FORM);
    setError('');
    setIsDone(false);
    onClose();
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!form.current || !form.next || !form.confirm) {
      setError('יש למלא את כל השדות');
      return;
    }
    const ruleError = passwordRuleError(form.next);
    if (ruleError) {
      setError(ruleError);
      return;
    }
    if (form.next !== form.confirm) {
      setError('הסיסמאות החדשות אינן תואמות');
      return;
    }
    if (form.next === form.current) {
      setError('הסיסמה החדשה צריכה להיות שונה מהנוכחית');
      return;
    }

    setIsSaving(true);
    setError('');
    try {
      const result = await changeEntraPasswordViaGraph(form.current, form.next);
      if (result.ok) {
        setForm(EMPTY_FORM);
        setIsDone(true);
      } else {
        console.error('Password change was rejected:', result.status, result.error?.code);
        setError(graphErrorMessage(result.error));
      }
    } catch (requestError) {
      console.error('Password change failed:', requestError?.errorCode || requestError?.name);
      setError(clientErrorMessage(requestError));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={close}
      PaperProps={{ style: { borderRadius: '8px', padding: '16px', width: '100%', maxWidth: '420px' } }}
    >
      <DialogTitle style={{ textAlign: 'center', fontWeight: 'bold', color: '#103C6E' }}>
        שינוי סיסמה
      </DialogTitle>

      {isDone ? (
        <>
          <DialogContent style={{ textAlign: 'center' }} dir="rtl">
            <CheckCircle size={48} color="#1FB5A3" style={{ marginBottom: '12px' }} />
            <Typography style={{ fontWeight: 'bold', color: '#103C6E' }}>הסיסמה עודכנה בהצלחה</Typography>
            <Typography variant="body2" color="text.secondary" style={{ marginTop: '8px' }}>
              בכניסה הבאה יש להשתמש בסיסמה החדשה.
            </Typography>
          </DialogContent>
          <DialogActions style={dialogActionsStyle}>
            <Button onClick={close} style={primaryButtonStyle}>סגור</Button>
          </DialogActions>
        </>
      ) : (
        <form onSubmit={submit} noValidate>
          <DialogContent dir="rtl">
            <PasswordInput
              icon={passwordIcon}
              placeholder="סיסמה נוכחית"
              autoComplete="current-password"
              value={form.current}
              onChange={(e) => updateField('current', e.target.value)}
              autoFocus
            />
            <PasswordInput
              icon={passwordIcon}
              placeholder="סיסמה חדשה"
              autoComplete="new-password"
              value={form.next}
              onChange={(e) => updateField('next', e.target.value)}
            />
            <PasswordInput
              icon={passwordIcon}
              placeholder="אימות סיסמה חדשה"
              autoComplete="new-password"
              value={form.confirm}
              onChange={(e) => updateField('confirm', e.target.value)}
            />
            <Typography variant="body2" color="text.secondary" style={{ marginTop: '12px' }}>
              לפחות 8 תווים, ושילוב של שלושה מתוך: אותיות קטנות, אותיות גדולות, ספרות וסימנים.
            </Typography>
            {error && (
              <Typography color="error" role="alert" style={{ marginTop: '12px' }}>
                {error}
              </Typography>
            )}
          </DialogContent>
          <DialogActions style={dialogActionsStyle}>
            <Button onClick={close} style={secondaryButtonStyle} disabled={isSaving}>
              ביטול
            </Button>
            <Button type="submit" style={primaryButtonStyle} disabled={isSaving}>
              {isSaving ? 'שומר...' : 'שמור'}
            </Button>
          </DialogActions>
        </form>
      )}
    </Dialog>
  );
};

EntraChangePasswordDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default EntraChangePasswordDialog;
